import type {
  VisionProposalRoomContext,
  VisionProposalSharedContext,
} from "@/features/analytics/constants/events";
import type { RankedSpace } from "../types";
import { groupRoomsIntoCombos } from "./comboGrouping";

/** Room-scoped analytics context, minus the shared context the caller sends
 * separately (proposal/deal/device/segment). */
export type RoomTrackingContext = Omit<
  VisionProposalRoomContext,
  keyof VisionProposalSharedContext
>;

type TrackableSpace = Pick<
  RankedSpace,
  "id" | "partnerId" | "groupIds" | "isIndividual"
>;

/**
 * Map each space id to its room tracking context. posicao_no_ranking is the
 * index in `spaces` AS PASSED — callers must pass the original algorithm
 * order (suggestedSpaces before any user re-sorting). offer_variant has no
 * backend source yet and is always null until the API provides it.
 */
export function buildRoomTrackingContextMap(
  spaces: TrackableSpace[],
): Map<string, RoomTrackingContext> {
  const comboIdBySpaceId = new Map<string, string>();
  groupRoomsIntoCombos(spaces).forEach((group) => {
    if (group.kind === "combo") {
      group.spaces.forEach((s) => {
        // First-wins: a room can belong to several combos; attribute it to the
        // first (its anchor in display order) instead of silently overwriting.
        if (!comboIdBySpaceId.has(s.id)) {
          comboIdBySpaceId.set(s.id, String(group.comboId));
        }
      });
    }
  });
  const map = new Map<string, RoomTrackingContext>();
  spaces.forEach((space, index) => {
    map.set(space.id, {
      espaco_id: space.id,
      partner_id: space.partnerId ?? null,
      posicao_no_ranking: index,
      offer_variant: null,
      combo_id: comboIdBySpaceId.get(space.id) ?? null,
    });
  });
  return map;
}

/** Context for a space id, with a null-field fallback for ids outside the
 * ranked list (defensive — every UI surface should hold a ranked space). */
export function roomTrackingContextFor(
  map: Map<string, RoomTrackingContext>,
  spaceId: string,
): RoomTrackingContext {
  return (
    map.get(spaceId) ?? {
      espaco_id: spaceId,
      partner_id: null,
      posicao_no_ranking: null,
      offer_variant: null,
      combo_id: null,
    }
  );
}
